/* eslint-disable max-nested-callbacks */
/* eslint-disable no-undef */
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";

const getActiveState = (index, collapseAccordion, defaultActiveIndex) => {
    if (Array.isArray(defaultActiveIndex)) {
        return !(defaultActiveIndex.indexOf(index) > -1);
    }
    if (defaultActiveIndex === index) {
        return false;
    }
    return collapseAccordion;
};

const Accordion = ({ children, totalItems = 1, collapseAccordion = false, toggleAll = false, defaultActiveIndex = -1 }) => {
    const initialStates = useMemo(() =>
        Array.from({ length: totalItems }).map((_, index) =>
            getActiveState(index, collapseAccordion, defaultActiveIndex)
        ), [totalItems, collapseAccordion, defaultActiveIndex]);

    const [currentVisibleStates, setCurrentVisibleStates] = useState(initialStates);
    const [loading, setLoading] = useState(false);
    const [loadingIndex, setLoadingIndex] = useState(-1);

    const collapseAccordionRef = useRef(collapseAccordion);
    const toggleAllRef = useRef(toggleAll);

    useEffect(() => {
        collapseAccordionRef.current = collapseAccordion;
        toggleAllRef.current = toggleAll;
    }, [collapseAccordion, toggleAll]);

    const onAccordionClickHandler = useCallback(async ({ index }) => {
        setLoading(true);
        setLoadingIndex(index);

        await yieldToMain();
        setCurrentVisibleStates(prevStates => {
            const newStates = [...prevStates];

            if (toggleAllRef.current) {
                newStates[index] = !newStates[index];
            } else {
                for (let i = 0; i < newStates.length; i++) {
                    if (i === index) {
                        newStates[i] = !newStates[i];
                    } else if (collapseAccordionRef.current) {
                        newStates[i] = true;
                    }
                }
            }
            return newStates;
        });
        setLoading(false);
        setLoadingIndex(-1);
    }, []);

    const accordionProps = useMemo(() => ({
        onClick: onAccordionClickHandler,
        currentVisibleStates,
        loading,
        loadingIndex
    }), [onAccordionClickHandler, currentVisibleStates, loading, loadingIndex]);

    return (
        <ul>
            {children(accordionProps)}
        </ul>
    );
};

const Heading = React.memo(({ children, headingRef }) => {
    return <React.Fragment>{React.cloneElement(children, { ref: headingRef })}</React.Fragment>;
});

const Body = React.memo(({ children }) => {
    return <React.Fragment>{children}</React.Fragment>;
});

const Content = React.memo(({ children }) => {
    return <li>{children}</li>;
});

Accordion.propTypes = {
    children: PropTypes.func,
    totalItems: PropTypes.number,
    toggleAll: PropTypes.bool,
    collapseAccordion: PropTypes.bool,
    defaultActiveIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.array
    ])
};

Heading.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    headingRef: PropTypes.object
};

Body.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

Content.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

const MemoizedAccordion = React.memo(Accordion);
MemoizedAccordion.Content = Content;
MemoizedAccordion.Heading = Heading;
MemoizedAccordion.Body = Body;

export default MemoizedAccordion;
